import React from "react";
import { Briefcase, Zap, Brain, ArrowUpRight, Sparkles } from "lucide-react";
import { SectionHeader } from "@/sections/Services";

const BLOCKS = [
  {
    icon: Briefcase,
    title: "Client Solutions",
    copy: "Bespoke builds for businesses — tailored websites, mobile apps and platforms designed around real commercial goals.",
  },
  {
    icon: Zap,
    title: "Internal Ventures",
    copy: "Company-owned apps and platforms, developed under the Mackbe Tech umbrella to solve real-world problems.",
  },
  {
    icon: Brain,
    title: "AI-Enhanced Development",
    copy: "Faster ideation, prototyping and launch cycles — using AI to compress timelines without compromising quality.",
  },
];

const COMING_SOON = [
  { tag: "Vertical SaaS", title: "Operator platform", desc: "Workflow & member tooling" },
  { tag: "AI", title: "Assistant suite", desc: "Domain-specific AI agents" },
  { tag: "Marketplace", title: "Service network", desc: "Local SME marketplace" },
];

export default function ProductStudio() {
  return (
    <section
      id="studio"
      data-testid="product-studio-section"
      className="relative py-28 md:py-36 border-t border-white/[0.06]"
    >
      <div
        className="mk-orb"
        style={{ width: 480, height: 480, background: "#0A66FF", top: 100, right: -160, opacity: 0.25 }}
        aria-hidden
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <SectionHeader
              eyebrow="Product Studio"
              title="From client projects to owned digital products"
            />
            <div className="mt-7 space-y-5 text-[16px] text-slate-300 leading-relaxed max-w-lg">
              <p className="mk-reveal">
                Mackbe Tech is evolving into a product-focused technology studio.
              </p>
              <p className="mk-reveal" style={{ transitionDelay: "100ms" }}>
                Alongside client work, we design and develop our own digital
                products and platforms. This ensures our approach remains
                commercially grounded, technically current and focused on
                real-world application.
              </p>
              <p className="mk-reveal" style={{ transitionDelay: "200ms" }}>
                We don't just build for clients — we build with the same standards
                we apply to our own ventures.
              </p>
            </div>
            <div className="mt-9 mk-reveal">
              <a
                href="#contact"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white mk-underline-link"
                data-testid="studio-partner-link"
              >
                Partner with the studio
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-5">
            {BLOCKS.map((b, i) => (
              <FeatureBlock key={b.title} index={i} {...b} />
            ))}

            {/* Coming soon product cards */}
            <div className="mt-2 mk-reveal">
              <div className="flex items-center gap-3 mb-5">
                <Sparkles className="h-4 w-4 text-[#00D4FF]" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-slate-400 font-medium">
                  Studio ventures · Coming soon
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {COMING_SOON.map((c, i) => (
                  <ComingSoonCard key={c.title} {...c} index={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureBlock({ icon: Icon, title, copy, index }) {
  return (
    <div
      data-testid={`studio-block-${index}`}
      className="mk-reveal mk-card rounded-2xl p-7 md:p-8 flex gap-5 items-start"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-[#0A66FF]/30 to-transparent">
        <Icon className="h-5 w-5 text-[#00D4FF]" />
      </div>
      <div>
        <h3 className="font-display text-xl md:text-2xl tracking-tight text-white">
          {title}
        </h3>
        <p className="mt-2 text-[15px] text-slate-400 leading-relaxed max-w-xl">
          {copy}
        </p>
      </div>
    </div>
  );
}

function ComingSoonCard({ tag, title, desc, index }) {
  return (
    <div
      data-testid={`coming-soon-card-${index}`}
      className="relative mk-card rounded-xl p-5 overflow-hidden group"
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <div
        className="absolute inset-0 bg-gradient-to-br from-[#0A66FF]/15 via-transparent to-[#00D4FF]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        aria-hidden
      />
      <div className="relative">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
            {tag}
          </span>
          <span className="h-1.5 w-1.5 rounded-full bg-[#00D4FF] mk-pulse" />
        </div>
        <div className="mt-3 font-display text-[15px] text-white leading-tight">
          {title}
        </div>
        <div className="mt-1 text-[12px] text-slate-500">{desc}</div>
        <div className="mt-4 h-px w-full bg-gradient-to-r from-[#00D4FF]/40 via-[#0A66FF]/30 to-transparent" />
      </div>
    </div>
  );
}
